import React, { useMemo, useState, useLayoutEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { Nav } from "react-bootstrap";

import Logo from "../../assets/LogoInsurcloud_NoBackground.png";
import { CLIENT_ORDER, CLIENT_LOGOS, CLIENT_COLORS } from "../../config/clientConfig";
import "./Sidebar.css";
import FeedbackWidget from "../FeedbackWidget/FeedbackWidget";
import ClientLogo from "../ClientLogo/ClientLogo";

import { useAuth } from "../../contexts/AuthContext";
import { usePathname } from "../../hooks/usePathname";

function Sidebar({ darkMode, setDarkMode, collapsed, setCollapsed }) {
  const { user, hasAccessToClient, logout, isAdmin, isAgentSmith, isTransformTeam, hasTransformDashboard } = useAuth();
  const navigate = useNavigate();
  const pathname = usePathname();
  const tooltipTimeout = useRef(null);
  const [tooltipLabel, setTooltipLabel] = useState(null);
  const [tooltipY, setTooltipY] = useState(0);

  const envLabel = useMemo(() => {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return { label: 'LOCAL', color: '#1d6348' };
    if (host.startsWith('qa.')) return { label: 'QA', color: '#92510a' };
    if (host.startsWith('develop.')) return { label: 'DEV', color: '#1e3a6e' };
    return null;
  }, []);

  const visibleClients = useMemo(() => {
    if (!user) return [];
    const allowedClients = new Set((user.allowedClients || []).map((client) => String(client).toUpperCase()));
    return CLIENT_ORDER.filter((client) => allowedClients.has(client) && hasAccessToClient(client));
  }, [user, hasAccessToClient]);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [hoveredClient, setHoveredClient] = useState(null);
  const [showControlTower, setShowControlTower] = useState(() => {
    const p = window.location.pathname;
    return p.startsWith('/client/') || p === '/command-center' || p.startsWith('/command-center/') || p === '/control';
  });
  const [showValidations, setShowValidations] = useState(() => {
    const p = window.location.pathname;
    return p === '/validations' || p.startsWith('/validations/');
  });
  const [showReports, setShowReports] = useState(() => {
    const p = window.location.pathname;
    return p === '/reports' || p.startsWith('/reports/') || p.startsWith('/release-status/');
  });
  const [showReleaseStatus, setShowReleaseStatus] = useState(() => {
    return window.location.pathname.startsWith('/release-status/');
  });
  const [showDataValidations, setShowDataValidations] = useState(() => {
    return window.location.pathname.startsWith('/data-validations/');
  });
  const [showGlValidations, setShowGlValidations] = useState(() => {
    return window.location.pathname.startsWith('/data-validations/gl-validations');
  });
  const [mounted] = useState(true);

  useLayoutEffect(() => {
    const p = pathname;
    if (p.startsWith('/client/') || p === '/command-center' || p.startsWith('/command-center/') || p === '/control') {
      setShowControlTower(true);
      setShowValidations(false);
      setShowReports(false);
      setShowDataValidations(false);
    } else if (p === '/validations' || p.startsWith('/validations/')) {
      setShowValidations(true);
      setShowControlTower(false);
      setShowReports(false);
      setShowDataValidations(false);
    } else if (p === '/reports' || p.startsWith('/reports/') || p.startsWith('/release-status/')) {
      setShowReports(true);
      setShowControlTower(false);
      setShowValidations(false);
      setShowDataValidations(false);
      if (p.startsWith('/release-status/')) {
        setShowReleaseStatus(true);
      }
    } else if (pathname.startsWith('/data-validations/')) {
      setShowDataValidations(true);
      setShowControlTower(false);
      setShowValidations(false);
      setShowReports(false);
      if (pathname.startsWith('/data-validations/gl-validations')) {
        setShowGlValidations(true);
      }
    }
  }, [pathname]);

  const getClientLogo = (client) => CLIENT_LOGOS[client] || null;
  const getClientColor = (client) => CLIENT_COLORS[client] || '#6b7280';

  const handleSectionToggle = (section) => {
    if (section === 'controlTower') {
      setShowControlTower(!showControlTower);
      if (!showControlTower) { setShowValidations(false); setShowReports(false); setShowDataValidations(false); }
    } else if (section === 'validations') {
      setShowValidations(!showValidations);
      if (!showValidations) { setShowControlTower(false); setShowReports(false); setShowDataValidations(false); }
    } else if (section === 'reports') {
      setShowReports(!showReports);
      if (!showReports) { setShowControlTower(false); setShowValidations(false); setShowDataValidations(false); }
    } else if (section === 'dataValidations') {
      setShowDataValidations(!showDataValidations);
      if (!showDataValidations) { setShowControlTower(false); setShowValidations(false); setShowReports(false); }
    }
  };

  const showTooltip = (e, label) => {
    if (!collapsed) return;
    clearTimeout(tooltipTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipY(rect.top + rect.height / 2);
    setTooltipLabel(label);
  };

  const hideTooltip = () => {
    tooltipTimeout.current = setTimeout(() => setTooltipLabel(null), 100);
  };

  // ─── Icon Container (modern pill with glow) ───
  const IconBox = ({ icon, isActive, activeColor = "#86BC25", size = 20 }) => (
    <div className={`sb-icon-box ${isActive ? 'sb-icon-box--active' : ''}`} style={{
      '--icon-active-color': activeColor,
    }}>
      <i className={`bi ${icon}`} style={{ fontSize: size }} />
    </div>
  );

  // ─── Section Header ───
  const renderSectionHeader = (label, icon, isOpen, activeColor, onClick) => (
    <li className="sb-nav-item">
      <div
        className={`sb-section-header ${isOpen ? 'sb-section-header--active' : ''}`}
        onClick={onClick}
        onMouseEnter={(e) => showTooltip(e, label)}
        onMouseLeave={hideTooltip}
        style={{ '--section-color': activeColor }}
      >
        <IconBox icon={icon} isActive={isOpen} activeColor={activeColor} />
        {!collapsed && (
          <>
            <span className="sb-section-label">{label}</span>
            <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} sb-section-chevron`} />
          </>
        )}
      </div>
    </li>
  );

  // ─── Top-level Link ───
  const renderTopLevelLink = (label, icon, to, activeColor) => (
    <li className="sb-nav-item">
      <NavLink
        to={to}
        className={({ isActive }) => `sb-section-header ${isActive ? 'sb-section-header--active' : ''}`}
        onMouseEnter={(e) => showTooltip(e, label)}
        onMouseLeave={hideTooltip}
        style={{ '--section-color': activeColor, textDecoration: 'none' }}
      >
        <IconBox icon={icon} isActive={pathname === to} activeColor={activeColor} />
        {!collapsed && <span className="sb-section-label">{label}</span>}
      </NavLink>
    </li>
  );

  // ─── Sub-item ───
  const renderSubItem = (label, icon, to, accentColor, adminOnly = false, itemIndex = 0) => {
    if (adminOnly && !isAdmin) return null;
    const isActive = pathname === to;
    const bg = isActive
      ? `linear-gradient(180deg, rgba(${accentColor.replace('#', '').match(/../g).map(h => parseInt(h, 16)).join(', ')}, 0.18) 0%, rgba(${accentColor.replace('#', '').match(/../g).map(h => parseInt(h, 16)).join(', ')}, 0.08) 100%)`
      : "transparent";
    return (
      <NavLink
        to={to}
        className={({ isActive }) => `sidebar-subitem${isActive ? " active-link" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 16px",
          marginLeft: "28px",
          marginBottom: "8px",
          borderRadius: 8,
          textDecoration: "none",
          background: isActive ? bg : "rgba(255,255,255,0.025)",
          border: isActive
            ? `1px solid ${accentColor}66`
            : "1px solid rgba(255,255,255,0.06)",
          borderLeft: isActive ? `4px solid ${accentColor}` : "4px solid transparent",
          boxShadow: isActive
            ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 4px rgba(0,0,0,0.25)"
            : "inset 0 1px 0 rgba(255,255,255,0.03), 0 1px 2px rgba(0,0,0,0.18)",
          color: isActive ? "#fff" : "#E8E8E3",
          fontWeight: isActive ? 600 : 400,
          fontSize: 14,
        }}
      >
        <i className={`bi ${icon}`} style={{ fontSize: 16, width: 20, textAlign: "center", flexShrink: 0, color: isActive ? accentColor : "#A7A8AA" }} />
        <span>{label}</span>
      </NavLink>
    );
  };

  // ─── Collapsible Sub-item (e.g. Release Status) ───
  const renderCollapsibleSubItem = (label, icon, baseTo, accentColor, isOpen, setOpen, clientListPrefix, itemIndex = 3) => {
    const isActivePath = pathname.startsWith(baseTo);
    const subItemStyle = {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 16px",
      marginLeft: "28px",
      marginBottom: "8px",
      borderRadius: 8,
      textDecoration: "none",
      background: isActivePath
        ? `linear-gradient(180deg, rgba(${accentColor.replace('#', '').match(/../g).map(h => parseInt(h, 16)).join(', ')}, 0.18) 0%, rgba(${accentColor.replace('#', '').match(/../g).map(h => parseInt(h, 16)).join(', ')}, 0.08) 100%)`
        : "rgba(255,255,255,0.025)",
      border: isActivePath
        ? `1px solid ${accentColor}66`
        : "1px solid rgba(255,255,255,0.06)",
      borderLeft: isActivePath ? `4px solid ${accentColor}` : "4px solid transparent",
      boxShadow: isActivePath
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 4px rgba(0,0,0,0.25)"
        : "inset 0 1px 0 rgba(255,255,255,0.03), 0 1px 2px rgba(0,0,0,0.18)",
      color: isActivePath ? "#fff" : "#E8E8E3",
      fontWeight: isActivePath ? 600 : 400,
      fontSize: 14,
    };
    const chevron = (
      <i
        className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} release-status-chevron`}
        style={{ fontSize: 12, width: 16, textAlign: "center", flexShrink: 0, color: "#A7A8AA" }}
      />
    );
    const iconEl = <i className={`bi ${icon}`} style={{ fontSize: 16, width: 20, textAlign: "center", flexShrink: 0, color: isActivePath ? accentColor : "#A7A8AA" }} />;

    return (
      <div style={{ display: "contents" }}>
        {isAdmin ? (
          <NavLink
            to={`${baseTo}/all`}
            onClick={(e) => { if (e.target.closest('.release-status-chevron')) { e.preventDefault(); setOpen(!isOpen); } }}
            className={({ isActive }) => `sidebar-subitem${isActive ? " active-link" : ""}`}
            style={subItemStyle}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {iconEl}
              <span>{label}</span>
            </div>
            {chevron}
          </NavLink>
        ) : (
          <div onClick={() => setOpen(!isOpen)} className="sidebar-subitem" style={{ ...subItemStyle, cursor: "pointer" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {iconEl}
              <span>{label}</span>
            </div>
            {chevron}
          </div>
        )}
        <div className={`sidebar-collapse-grid${isOpen ? ' sidebar-collapse-open' : ''}`}
          style={{ marginLeft: "28px" }}
        >
          <div className="sidebar-collapse-inner">
            {renderClientList(clientListPrefix, baseTo, isOpen, accentColor)}
          </div>
        </div>
      </div>
    );
  };

  // ─── Client List ───
  const renderClientList = (prefix, basePath, isOpen, accentColor) => (
    <div style={{ paddingBottom: isOpen ? "12px" : "0" }}>
      {visibleClients.map((c, idx) => {
        const isActive = pathname === `${basePath}/${c}`;
        const color = getClientColor(c);
        return (
          <li key={`${prefix}-${c}`} style={{ listStyle: "none", paddingLeft: "32px" }}>
            <NavLink
              to={`${basePath}/${c}`}
              className={({ isActive }) => `sidebar-client-item${isActive ? " active-link" : ""}`}
              onMouseEnter={() => setHoveredClient(`${prefix}-${c}`)}
              onMouseLeave={() => setHoveredClient(null)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "9px 14px",
                marginBottom: 8,
                borderRadius: 8,
                cursor: "pointer",
                background: isActive
                  ? `linear-gradient(90deg, ${color}2e 0%, ${color}14 100%)`
                  : hoveredClient === `${prefix}-${c}`
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(255,255,255,0.025)",
                border: isActive
                  ? `1px solid ${accentColor || color}66`
                  : "1px solid rgba(255,255,255,0.06)",
                borderLeft: isActive ? `4px solid ${accentColor || color}` : "4px solid transparent",
                boxShadow: isActive
                  ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 4px rgba(0,0,0,0.25)"
                  : "inset 0 1px 0 rgba(255,255,255,0.03), 0 1px 2px rgba(0,0,0,0.18)",
                textDecoration: "none",
                color: isActive ? "#fff" : "#E8E8E3",
                fontWeight: isActive ? 700 : 400,
                transform: hoveredClient === `${prefix}-${c}` && !isActive ? "translateX(2px)" : "translateX(0)",
              }}
            >
              <ClientLogo
                client={c}
                src={getClientLogo(c)}
                active={isActive}
                activeColor={accentColor || color}
                fallbackText={c.substring(0, 2)}
                fallbackColor={isActive ? (accentColor || color) : color}
              />
              <span style={{ fontSize: 14 }}>{c}</span>
            </NavLink>
          </li>
        );
      })}
    </div>
  );

  // ─── GL Validations: collapsible item with CMIG child ───
  const renderGlValidationsSubItem = () => {
    const accentColor = "#86BC25";
    const isActiveSection = pathname.startsWith('/data-validations/gl-validations');
    const cmigColor = getClientColor('CMIG');
    const cmigLogo = getClientLogo('CMIG');
    const isCmigActive = pathname === '/data-validations/gl-validations';

    return (
      <div style={{ display: "contents" }}>
        <div
          onClick={() => setShowGlValidations(!showGlValidations)}
          className="sidebar-subitem"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 16px",
            marginLeft: "28px",
            marginBottom: "8px",
            borderRadius: 8,
            cursor: "pointer",
            background: isActiveSection
              ? `linear-gradient(180deg, rgba(134,188,37,0.18) 0%, rgba(134,188,37,0.08) 100%)`
              : "rgba(255,255,255,0.025)",
            border: isActiveSection ? `1px solid ${accentColor}66` : "1px solid rgba(255,255,255,0.06)",
            borderLeft: isActiveSection ? `4px solid ${accentColor}` : "4px solid transparent",
            boxShadow: isActiveSection
              ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 4px rgba(0,0,0,0.25)"
              : "inset 0 1px 0 rgba(255,255,255,0.03), 0 1px 2px rgba(0,0,0,0.18)",
            color: isActiveSection ? "#fff" : "#E8E8E3",
            fontWeight: isActiveSection ? 600 : 400,
            fontSize: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <i className="bi bi-file-earmark-text" style={{ fontSize: 16, width: 20, textAlign: "center", flexShrink: 0, color: isActiveSection ? accentColor : "#A7A8AA" }} />
            <span>GL Validations</span>
          </div>
          <i
            className={`bi ${showGlValidations ? "bi-chevron-up" : "bi-chevron-down"}`}
            style={{ fontSize: 12, width: 16, textAlign: "center", flexShrink: 0, color: "#A7A8AA", marginLeft: "auto" }}
          />
        </div>
        <div className={`sidebar-collapse-grid${showGlValidations ? ' sidebar-collapse-open' : ''}`} style={{ marginLeft: "28px" }}>
          <div className="sidebar-collapse-inner">
            <div style={{ paddingBottom: showGlValidations ? "12px" : "0" }}>
              <li style={{ listStyle: "none", paddingLeft: "32px" }}>
                <NavLink
                  to="/data-validations/gl-validations"
                  className={({ isActive }) => `sidebar-client-item${isActive ? " active-link" : ""}`}
                  onMouseEnter={() => setHoveredClient('gl-CMIG')}
                  onMouseLeave={() => setHoveredClient(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "9px 14px",
                    marginBottom: 8,
                    borderRadius: 8,
                    cursor: "pointer",
                    background: isCmigActive
                      ? `linear-gradient(90deg, ${cmigColor}2e 0%, ${cmigColor}14 100%)`
                      : hoveredClient === 'gl-CMIG'
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(255,255,255,0.025)",
                    border: isCmigActive
                      ? `1px solid ${accentColor}66`
                      : "1px solid rgba(255,255,255,0.06)",
                    borderLeft: isCmigActive ? `4px solid ${accentColor}` : "4px solid transparent",
                    boxShadow: isCmigActive
                      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 4px rgba(0,0,0,0.25)"
                      : "inset 0 1px 0 rgba(255,255,255,0.03), 0 1px 2px rgba(0,0,0,0.18)",
                    textDecoration: "none",
                    color: isCmigActive ? "#fff" : "#E8E8E3",
                    fontWeight: isCmigActive ? 700 : 400,
                    transform: hoveredClient === 'gl-CMIG' && !isCmigActive ? "translateX(2px)" : "translateX(0)",
                  }}
                >
                  <ClientLogo
                    client="CMIG"
                    src={cmigLogo}
                    active={isCmigActive}
                    activeColor={accentColor}
                    fallbackText="CM"
                    fallbackColor={isCmigActive ? accentColor : cmigColor}
                  />
                  <span style={{ fontSize: 14 }}>CMIG</span>
                </NavLink>
              </li>
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <>
      <div className={`sb-root ${collapsed ? 'sb-root--collapsed' : ''}`}>
        {/* ─── Ambient glow line ─── */}
        <div className="sb-glow-line" />

        {/* ─── Logo Header ─── */}
        <div className="sb-logo-header">
          {collapsed ? (
            <div className="sb-logo-icon" onClick={() => navigate('/control')}>
              <span>IC</span>
            </div>
          ) : (
            <img
              className="sb-logo-img"
              src={Logo}
              alt="Logo"
              onClick={() => navigate('/control')}
            />
          )}
        </div>

        {/* ─── Environment Banner ─── */}
        {envLabel && (
          <div className="sb-env-badge" style={{ background: envLabel.color }}>
            {collapsed ? envLabel.label[0] : `🛠️ ${envLabel.label} ENV`}
          </div>
        )}

        {/* ─── Navigation ─── */}
        <div className="sidebar-scroll sb-nav-area">
          <Nav className="sb-nav-list">
            {/* ── Separator: Main ── */}
            {!collapsed && <li className="sb-separator"><span>Main</span></li>}

            {renderSectionHeader("Command Center", "bi-grid-1x2-fill", showControlTower, "#86BC25", () => { handleSectionToggle('controlTower'); navigate('/command-center'); })}
            {!collapsed && (
              <div className={`sidebar-collapse-grid${mounted && showControlTower ? ' sidebar-collapse-open' : ''}`}>
                <div className="sidebar-collapse-inner">
                  {renderClientList("tower", "/client", showControlTower)}
                </div>
              </div>
            )}

            {renderSectionHeader("Daily Validations", "bi-patch-check-fill", showValidations, "#86BC25", () => { handleSectionToggle('validations'); navigate('/validations'); })}
            {!collapsed && (
              <div className={`sidebar-collapse-grid${mounted && showValidations ? ' sidebar-collapse-open' : ''}`}>
                <div className="sidebar-collapse-inner">
                  {renderClientList("val", "/validations", showValidations)}
                </div>
              </div>
            )}

            {/* ── Separator: Data ── */}
            {!collapsed && <li className="sb-separator"><span>Data</span></li>}

            {renderSectionHeader("Data Validations", "bi-database-fill-check", showDataValidations, "#86BC25", () => { handleSectionToggle('dataValidations'); navigate('/data-validations'); })}
            {!collapsed && (
              <div className={`sidebar-collapse-grid${showDataValidations ? ' sidebar-collapse-open' : ''}`}>
                <div className="sidebar-collapse-inner">
                  {renderSubItem("ETL Jobs", "bi-arrow-repeat", "/data-validations/etl-jobs", "#86BC25", false)}
                  {hasAccessToClient('CMIG') && renderGlValidationsSubItem()}
                </div>
              </div>
            )}

            {/* ── Separator: Analytics ── */}
            {!collapsed && <li className="sb-separator"><span>Analytics</span></li>}

            {renderSectionHeader("Reports", "bi-bar-chart-line-fill", showReports, "#86BC25", () => { handleSectionToggle('reports'); navigate("/reports"); })}
            {!collapsed && (
              <div className={`sidebar-collapse-grid${mounted && showReports ? ' sidebar-collapse-open' : ''}`}>
                <div className="sidebar-collapse-inner">
                  {hasTransformDashboard && renderSubItem("DoD", "bi-check2-circle", "/reports/dod", "#86BC25", false, 0)}
                  {hasTransformDashboard && renderSubItem("Data Quality Report", "bi-arrow-left-right", "/reports/mismatch-report", "#86BC25", false, 1)}
                  {hasTransformDashboard && renderSubItem("L2 Performance", "bi-speedometer2", "/reports/team-performance", "#86BC25", false, 2)}
                  {renderSubItem("IC Police", "bi-shield-fill-exclamation", "/reports/ic-police", "#86BC25", false, 3)}
                  {renderCollapsibleSubItem("Release Status", "bi-rocket-takeoff-fill", "/reports/release-status", "#86BC25", showReleaseStatus, setShowReleaseStatus, "rs", 4)}
                  {isTransformTeam && renderSubItem("Transform KPIs", "bi-graph-up-arrow", "/reports/kpi-dashboard", "#86BC25", false, 5)}
                  {isTransformTeam && renderSubItem("Incident Failures", "bi-clipboard-data", "/reports/incident-failure-audit", "#86BC25", false, 10)}
                  {hasTransformDashboard && renderSubItem("QA Dashboard", "bi-clipboard2-check", "/reports/qa-dashboard", "#86BC25", false, 6)}
                  {hasTransformDashboard && renderSubItem("Data Dashboard", "bi-database-gear", "/reports/data-team-dashboard", "#86BC25", false, 7)}
                  {renderSubItem("Report Builder", "bi-tools", "/reports/report-builder", "#86BC25", true, 8)}
                  {(isAgentSmith || isAdmin) && renderSubItem("Agent Smith Metrics", "bi-bar-chart-line-fill", "/reports/agent-smith-metrics", "#86BC25", false, 9)}

                </div>
              </div>
            )}

            {!collapsed && <li className="sb-separator"><span>AI</span></li>}
            {(isAgentSmith || isAdmin) && (
              <>
                {renderTopLevelLink("Agent Smith", "bi-cpu-fill", "/agent-smith", "#86BC25")}
                {renderTopLevelLink("QA Neo", "bi-clipboard2-check-fill", "/qa-neo", "#8B5CF6")}
                {renderTopLevelLink("Trinity", "bi-bug-fill", "/gw-bugfixer", "#00c896")}
              </>
            )}
            {renderTopLevelLink("IRIS", "bi-stars", "/iris", "#ec4899")}
          </Nav>
        </div>

        {/* ─── User Footer ─── */}
        <div className="sb-footer">
          {/* User menu dropdown */}
          {userMenuOpen && !collapsed && (
            <div className="sb-user-menu">
              <div className="sb-user-menu-item sb-user-menu-item--settings" onClick={() => { setUserMenuOpen(false); navigate("/settings"); }}>
                <i className="bi bi-gear-fill" />
                <span>Settings</span>
              </div>
              <div className="sb-user-menu-item sb-user-menu-item--feedback" onClick={() => { setUserMenuOpen(false); setFeedbackOpen(true); }}>
                <i className="bi bi-lightbulb-fill" />
                <span>Share Feedback</span>
              </div>
              <div className="sb-user-menu-item sb-user-menu-item--logout" onClick={() => { logout(); navigate("/login", { replace: true }); }}>
                <i className="bi bi-box-arrow-right" />
                <span>Logout</span>
              </div>
            </div>
          )}

          {user && (
            <div className={`sb-user-card ${userMenuOpen ? 'sb-user-card--open' : ''}`} onClick={() => setUserMenuOpen(o => !o)}>
              <div className="sb-user-avatar">
                {user.name?.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()
                  || user.username?.[0]?.toUpperCase()
                  || "?"}
              </div>
              {!collapsed && (
                <>
                  <div className="sb-user-info">
                    <div className="sb-user-name">{user.name || user.username}</div>
                    <div className="sb-user-role">{user.displayRole || user.role || "user"}</div>
                  </div>
                  <i className={`bi bi-chevron-${userMenuOpen ? 'down' : 'up'} sb-user-chevron`} />
                </>
              )}
            </div>
          )}
        </div>

        {/* ─── Tooltip (collapsed mode) ─── */}
        {collapsed && tooltipLabel && (
          <div className="sb-tooltip" style={{ top: tooltipY }}>
            {tooltipLabel}
          </div>
        )}
      </div>
      <FeedbackWidget open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
}

export default Sidebar;
